import { ChangeEvent, useEffect, useMemo, useState } from "react";

import { Button } from "@components/Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/Dialog";
import { Field, FieldLabel } from "@components/Field";
import { Input } from "@components/Input";
import { Stack } from "@components/Stack";
import { Typography } from "@components/Typography";

import type { TCustomStream, TCustomStreamLink } from "@api/customStream";

import { useAddCustomStreamLinkMutation, useEditCustomStreamLinkMutation } from "@storage/queries/customStream";

type TCustomStreamDialogMode = "create" | "edit";

interface CustomStreamDialogProps {
	mode: TCustomStreamDialogMode;
	stream?: TCustomStream;
	onClose: () => void;
}

type TField = {
	label: string;
	value: string;
	onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

export const CustomStreamDialog = ({ mode, stream, onClose }: CustomStreamDialogProps) => {
	const addCustomStreamLinkMutation = useAddCustomStreamLinkMutation();
	const editCustomStreamLinkMutation = useEditCustomStreamLinkMutation();

	const [title, setTitle] = useState("");
	const [vrLink, setVrLink] = useState("");
	const [twoDLink, setTwoDLink] = useState("");

	const isPending = addCustomStreamLinkMutation.isPending || editCustomStreamLinkMutation.isPending;

	const linkByType = useMemo(() => {
		const map: Partial<Record<TCustomStreamLink["type"], TCustomStreamLink>> = {};
		stream?.links.forEach((item) => {
			map[item.type] = item;
		});
		return map;
	}, [stream]);

	useEffect(() => {
		setTitle(stream?.title ?? "");
		setVrLink(linkByType["3D"]?.link ?? "");
		setTwoDLink(linkByType["2D"]?.link ?? "");
	}, [linkByType, stream?.title]);

	const fields: TField[] = [
		{
			label: "Platform",
			value: title,
			onChange: (event) => setTitle(event.target.value),
		},
		{
			label: "VR",
			value: vrLink,
			onChange: (event) => setVrLink(event.target.value),
		},
		{
			label: "2D",
			value: twoDLink,
			onChange: (event) => setTwoDLink(event.target.value),
		},
	];

	const handleSave = () => {
		const trimmedTitle = title.trim();
		const trimmedVrLink = vrLink.trim();
		const trimmedTwoDLink = twoDLink.trim();

		const links = [
			trimmedVrLink
				? {
						id: linkByType["3D"]?.id,
						type: "3D" as const,
						link: trimmedVrLink,
					}
				: null,
			trimmedTwoDLink
				? {
						id: linkByType["2D"]?.id,
						type: "2D" as const,
						link: trimmedTwoDLink,
					}
				: null,
		].filter((item): item is NonNullable<typeof item> => item !== null);

		if (mode === "edit" && stream?.id) {
			editCustomStreamLinkMutation.mutate(
				{
					id: stream.id,
					title: trimmedTitle,
					links,
				},
				{
					onSuccess: onClose,
				},
			);
			return;
		}

		addCustomStreamLinkMutation.mutate(
			{
				title: trimmedTitle,
				links: links.map(({ id: _id, ...link }) => link),
			},
			{
				onSuccess: onClose,
			},
		);
	};

	return (
		<Dialog open onOpenChange={onClose}>
			<DialogContent className="max-w-110 pb-6">
				<DialogHeader>
					<DialogTitle>Custom stream</DialogTitle>
				</DialogHeader>

				<Stack spacing={12}>
					{fields.map((item) => (
						<Stack key={item.label} spacing={4} wide>
							<Field>
								<FieldLabel htmlFor={item.label}>
									<Typography size="xs" weight="bold">
										{item.label}
									</Typography>
								</FieldLabel>
								<Input id={item.label} value={item.value} onChange={item.onChange} />
							</Field>
						</Stack>
					))}
				</Stack>

				<Stack direction="row" spacing={8} wide className="mt-5">
					<Button type="secondary" onClick={onClose} disable={isPending} wide>
						<Typography size="sm" weight="bold">
							Cancel
						</Typography>
					</Button>
					<Button onClick={handleSave} disable={isPending} wide>
						<Typography size="sm" weight="bold">
							Save
						</Typography>
					</Button>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};
